import React, { useState } from "react";
import { useAuthQuery as useQuery } from '../../../shared/hooks/useAuthQuery';
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { Button } from "../../../catalyst-components/button";
import { Badge } from "../../../catalyst-components/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../../catalyst-components/table";
import { AdminRoutes, MainRoutes } from "../../../shared/models";
import { Id } from "../../../convex/_generated/dataModel";
import { parseMarkdown } from "../../../shared/helpers/markdown";
import { InnerWeatherIcon } from "../../../components/InnerWeatherIcon";
import { useInnerWeatherQuote } from "../../../shared/hooks/useInnerWeatherQuote";
import { MindsetTrendChart } from "../../../shared/components/MindsetTrendChart";
import { ChatBubbleLeftRightIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { formatDuration } from "../../../shared/utils/formatDuration";
import { WidgetRenderer, DebugTraceDisplay } from "../../evergreen-chat/components/EvergreenMessage";

const formatValue = (value: string | undefined) => {
  if (!value) return '<span class="text-gray-400 italic">Not provided</span>';

  // Check if the value contains semicolons
  if (value.includes(';')) {
    // Split by semicolon, trim each item, and convert to markdown list
    const items = value.split(';')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => `* ${item}`)
      .join('\n');
    return parseMarkdown(items);
  }

  return parseMarkdown(value);
};

const SectionHeader = ({ title, icon }: { title: string; icon: string }) => (
  <div className="flex items-center gap-2 mb-4">
    <span className="text-2xl">{icon}</span>
    <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
  </div>
);

const FieldItem = ({ label, value }: { label: string; value: string }) => (
  <div className="mb-4 last:mb-0">
    <h4 className="text-sm font-semibold text-primary-5 uppercase tracking-wide mb-1">{label}</h4>
    <div
      className="text-gray-800 text-sm leading-relaxed prose prose-sm max-w-none"
      dangerouslySetInnerHTML={{
        __html: formatValue(value)
      }}
    />
  </div>
);

const InfoCard = ({ title, children, className = "" }: { title: string; children: React.ReactNode; className?: string }) => (
  <div className={`bg-primary-1 rounded-lg shadow-sm border border-primary-4 p-4 sm:p-6 ${className}`}>
    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-4">{title}</h3>
    {children}
  </div>
);

// Completed Sessions Table Component
const CompletedSessionsTable = ({ userId }: { userId: Id<"users"> }) => {
  const navigate = useNavigate();
  const user = useQuery(api.users.getUserById, { id: userId });
  const completedSessions = useQuery(api.completed_sessions.listByUser, { user_id: userId }) || [];

  return (
    <div>
      {completedSessions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No completed sessions found for this user.
        </div>
      ) : (
        <Table striped className="w-full">
          <TableHead className="border-b-2 border-primary-2">
            <TableRow>
              <TableHeader>Actions</TableHeader>
              <TableHeader>Skill</TableHeader>
              <TableHeader>Assistant</TableHeader>
              <TableHeader>Completed</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {completedSessions.map((session) => {
              return (
                <TableRow key={session._id} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        color="light"
                        onClick={() => navigate(`${MainRoutes.Chat}?sessionId=${session.session_id}&name=${user?.firstName} ${user?.lastName}&readonly=true`)}
                      >
                        View Chat
                      </Button>
                      <Button
                        color="light"
                        onClick={() => navigate(`/admin/sessions/${session.session_id}/insights?from=user&userId=${userId}`)}
                      >
                        Insights
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    {session.skill_name}
                  </TableCell>
                  <TableCell className="font-medium">
                    {session.assistant_name}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {session.completed_timestamp 
                      ? new Date(session.completed_timestamp).toLocaleDateString()
                      : 'N/A'
                    }
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

// Checkin Conversation Viewer - shows the actual chat messages for a checkin
const CheckinConversation = ({ userId, checkinId }: { userId: Id<"users">; checkinId: Id<"checkin_instructions"> }) => {
  const contextId = `checkin_${checkinId}`;
  const messages = useQuery(api.evergreen_chat.getMessagesByContext, {
    user_id: userId,
    context_id: contextId,
  });

  if (messages === undefined) {
    return (
      <div className="flex justify-center py-4">
        <div className="text-sm text-gray-400">Loading conversation...</div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="text-sm text-gray-400 italic">No conversation messages found for this check-in.</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
      {messages.map((msg) => (
        <div
          key={msg._id}
          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
              msg.role === 'user'
                ? 'bg-primary/10 text-gray-900 rounded-br-md'
                : 'bg-gray-100 text-gray-800 rounded-bl-md'
            }`}
          >
            <div className="text-xs font-medium mb-1 opacity-60">
              {msg.role === 'user' ? 'User' : 'Assistant'}
            </div>
            <div
              className="text-sm leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }}
            />
            <div className="text-[10px] text-gray-400 mt-1 text-right">
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Evergreen Chat Viewer - shows all evergreen messages with acorn costs
const EvergreenChatViewer = ({ userId }: { userId: Id<"users"> }) => {
  const messages = useQuery(api.evergreen_chat.adminGetMessages, { user_id: userId, limit: 200 });

  if (messages === undefined) {
    return (
      <div className="flex justify-center py-8">
        <div className="text-sm text-gray-400">Loading messages...</div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No evergreen chat messages found for this user.
      </div>
    );
  }

  // Group messages by date
  const groupedByDate: Record<string, typeof messages> = {};
  for (const msg of messages) {
    const date = new Date(msg.timestamp).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    if (!groupedByDate[date]) groupedByDate[date] = [];
    groupedByDate[date].push(msg);
  }

  return (
    <div className="space-y-6">
      {Object.entries(groupedByDate).map(([date, msgs]) => (
        <div key={date}>
          <div className="sticky top-0 z-10 flex justify-center mb-3">
            <span className="bg-gray-200 text-gray-600 text-xs font-medium px-3 py-1 rounded-full">
              {date}
            </span>
          </div>
          <div className="space-y-3">
            {msgs.map((msg) => (
              <div
                key={msg._id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-primary/10 text-gray-900 rounded-br-md'
                      : msg.role === 'system'
                        ? 'bg-yellow-50 text-gray-800 rounded-bl-md border border-yellow-200'
                        : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium opacity-60">
                      {msg.role === 'user' ? 'User' : msg.role === 'system' ? 'System' : 'Assistant'}
                    </span>
                    {msg.acorn_cost > 0 && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                        -{msg.acorn_cost} acorn{msg.acorn_cost !== 1 ? 's' : ''}
                      </span>
                    )}
                    {msg.acorn_balance !== null && msg.acorn_balance !== undefined && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                        bal: {msg.acorn_balance}
                      </span>
                    )}
                  </div>
                  <div
                    className="text-sm leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }}
                  />
                  {/* Widgets with blocking overlay */}
                  {(() => {
                    const allWidgets: Array<{ type: string; data: any }> = [];
                    if ((msg as any).widgets?.length > 0) allWidgets.push(...(msg as any).widgets);
                    else if ((msg as any).widget) allWidgets.push((msg as any).widget);
                    return allWidgets.length > 0 ? (
                      <div className="mt-2 space-y-2 relative">
                        <div className="pointer-events-none">
                          {allWidgets.map((widget, idx) => (
                            <div key={`${widget.type}-${idx}`} className="opacity-80">
                              <WidgetRenderer widget={widget} onQuickReply={() => {}} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}
                  {/* Debug trace */}
                  {(msg as any).debug_trace?.length > 0 && (
                    <div className="mt-2">
                      <DebugTraceDisplay trace={(msg as any).debug_trace} />
                    </div>
                  )}
                  <div className="text-[10px] text-gray-400 mt-1 text-right">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// User Checkins Table Component
const UserCheckinsTable = ({ userId }: { userId: Id<"users"> }) => {
  const checkins = useQuery(api.user_checkins.getUserCheckinHistory, { user_id: userId }) || [];
  const [expandedCheckins, setExpandedCheckins] = useState<Set<string>>(new Set());

  const toggleExpanded = (checkinRecordId: string) => {
    setExpandedCheckins(prev => {
      const next = new Set(prev);
      if (next.has(checkinRecordId)) {
        next.delete(checkinRecordId);
      } else {
        next.add(checkinRecordId);
      }
      return next;
    });
  };

  const getMoodBadgeColor = (mood: string | undefined) => {
    switch (mood) {
      case 'very_positive': return 'green';
      case 'positive': return 'lime';
      case 'neutral': return 'zinc';
      case 'struggling': return 'orange';
      case 'very_struggling': return 'red';
      default: return 'zinc';
    }
  };

  const formatMood = (mood: string | undefined) => {
    if (!mood) return 'N/A';
    return mood.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'pending': return 'yellow';
      case 'dismissed': return 'zinc';
      default: return 'zinc';
    }
  };

  return (
    <div>
      {checkins.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No check-ins found for this user.
        </div>
      ) : (
        <div className="space-y-4">
          {checkins.map((checkin) => {
            const isExpanded = expandedCheckins.has(checkin._id);
            const isCompleted = checkin.status === 'completed';

            return (
              <div key={checkin._id} className="border rounded-lg p-4 bg-white">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge color={getStatusBadgeColor(checkin.status)}>
                        {checkin.status.charAt(0).toUpperCase() + checkin.status.slice(1)}
                      </Badge>
                      {checkin.mood && (
                        <Badge color={getMoodBadgeColor(checkin.mood)}>
                          {formatMood(checkin.mood)}
                        </Badge>
                      )}
                      <span className="text-sm text-gray-500">
                        Every {checkin.checkin_frequency} session{checkin.checkin_frequency !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 italic">
                      "{checkin.checkin_greeting}"
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="text-right text-sm text-gray-500">
                      {checkin.shown_at && (
                        <div>Shown: {new Date(checkin.shown_at).toLocaleDateString()}</div>
                      )}
                      {checkin.completed_at && (
                        <div>Completed: {new Date(checkin.completed_at).toLocaleDateString()}</div>
                      )}
                      {checkin.dismissed_at && (
                        <div>Dismissed: {new Date(checkin.dismissed_at).toLocaleDateString()}</div>
                      )}
                    </div>
                    {isCompleted && (
                      <Button
                        color="light"
                        className="flex items-center gap-1 text-xs shrink-0"
                        onClick={() => toggleExpanded(checkin._id)}
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                        {isExpanded ? 'Hide' : 'View'} Chat
                        {isExpanded ? (
                          <ChevronUpIcon className="h-3 w-3" />
                        ) : (
                          <ChevronDownIcon className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Conversation Section */}
                {isExpanded && isCompleted && (
                  <div className="mt-3 pt-3 border-t border-blue-100 bg-blue-50/30 -mx-4 -mb-4 px-4 pb-4 rounded-b-lg">
                    <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-1.5">
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      Check-in Conversation
                    </h4>
                    <CheckinConversation userId={userId} checkinId={checkin.checkin_id} />
                  </div>
                )}

                {/* Analysis Section */}
                {checkin.response_summary && (
                  <div className={`mt-3 pt-3 border-t ${isExpanded ? '' : ''}`}>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Analysis</h4>
                    <p className="text-sm text-gray-700 mb-2">{checkin.response_summary}</p>

                    {checkin.key_insights && checkin.key_insights.length > 0 && (
                      <div className="mb-2">
                        <span className="text-xs font-semibold text-gray-600">Key Insights:</span>
                        <ul className="list-disc list-inside text-sm text-gray-600 ml-2">
                          {checkin.key_insights.map((insight, idx) => (
                            <li key={idx}>{insight}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {checkin.action_items && checkin.action_items.length > 0 && (
                      <div>
                        <span className="text-xs font-semibold text-gray-600">Action Items:</span>
                        <ul className="list-disc list-inside text-sm text-gray-600 ml-2">
                          {checkin.action_items.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {checkin.analyzed_at && (
                      <div className="text-xs text-gray-400 mt-2">
                        Analyzed: {new Date(checkin.analyzed_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Component to display current session info
const CurrentSessionDisplay = ({ 
  userId, 
  sessionId
}: { 
  userId: Id<"users">; 
  sessionId: Id<"sessions">;
}) => {
  const session = useQuery(api.sessions.getSessionById, { 
    session_id: sessionId,
    user_id: userId,
    is_personal_assistant: false
  });

  const assistant = useQuery(
    api.assistants.getById, 
    session?.assistant_id ? { id: session.assistant_id } : "skip"
  );

  const skillAssistant = useQuery(
    api.skill_assistants.getByAssistantId, 
    session?.assistant_id ? { assistant_id: session.assistant_id } : "skip"
  );

  const skill = useQuery(
    api.skills.getById, 
    skillAssistant?.skill_id ? { id: skillAssistant.skill_id } : "skip"
  );

  if (!session) return null;

  const sessionStartTime = session._creationTime;
  const now = Date.now();
  const elapsed = now - sessionStartTime;
  const hours = Math.floor(elapsed / (1000 * 60 * 60));
  const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
  const elapsedDisplay = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  
  return (
    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-blue-600">Current Skill:</span>
          <span className="text-blue-800 font-medium">
            {skill?.name || 'Unknown Skill'}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-blue-600">Assistant:</span>
          <span className="text-blue-800 font-medium">
            {assistant?.display_name || assistant?.name || 'Unknown'}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-blue-600">Session Time:</span>
          <span className="text-blue-800 font-mono">
            {elapsedDisplay}
          </span>
        </div>
      </div>
    </div>
  );
};

// Mindset Score History Table Component
const MindsetScoreHistoryTable = ({ userId, userName }: { userId: Id<"users">; userName: string }) => {
  const navigate = useNavigate();
  const scoreHistory = useQuery(api.user_progress.getScoreHistory, { user_id: userId, limit: 100 }) || [];

  const getCompositeColor = (composite: number) => {
    if (composite >= 70) return 'text-emerald-600';
    if (composite >= 50) return 'text-amber-600';
    return 'text-red-500';
  };

  const getTrendIndicator = (current: number, previous: number | undefined) => {
    if (previous === undefined) return '';
    const diff = current - previous;
    if (diff > 2) return '↑';
    if (diff < -2) return '↓';
    return '→';
  };

  const getTrendColor = (current: number, previous: number | undefined) => {
    if (previous === undefined) return 'text-gray-400';
    const diff = current - previous;
    if (diff > 2) return 'text-emerald-500';
    if (diff < -2) return 'text-red-500';
    return 'text-gray-400';
  };

  // Reverse to show most recent first
  const reversed = [...scoreHistory].reverse();

  return (
    <div>
      {scoreHistory.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No mindset score history found for this user.
        </div>
      ) : (
        <>
          {/* Trend Indicator */}
          {scoreHistory.length >= 2 && (
            <div className="mb-4 p-3 bg-white rounded-lg border flex items-center justify-center gap-2">
              <span className="text-sm font-semibold text-gray-600">Overall Trend</span>
              <MindsetTrendChart data={scoreHistory} />
            </div>
          )}

          {/* History Table */}
          <Table striped className="w-full">
            <TableHead className="border-b-2 border-primary-2">
              <TableRow>
                <TableHeader>Date</TableHeader>
                <TableHeader className="text-center">Composite</TableHeader>
                <TableHeader className="text-center">N Score</TableHeader>
                <TableHeader className="text-center">WB Score</TableHeader>
                <TableHeader className="text-center">SE Score</TableHeader>
                <TableHeader className="text-center">Trend</TableHeader>
                <TableHeader>Session</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {reversed.map((record, idx) => {
                // The previous record in chronological order (reversed[idx+1] because we reversed)
                const prevRecord = idx < reversed.length - 1 ? reversed[idx + 1] : undefined;
                return (
                  <TableRow key={record._id} className="hover:bg-gray-50">
                    <TableCell className="text-sm text-gray-600">
                      {new Date(record.recorded_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`font-bold ${getCompositeColor(record.composite_score)}`}>
                        {record.composite_score.toFixed(1)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center font-mono text-sm">{record.n_score}</TableCell>
                    <TableCell className="text-center font-mono text-sm">{record.wb_score}</TableCell>
                    <TableCell className="text-center font-mono text-sm">{record.se_score}</TableCell>
                    <TableCell className="text-center">
                      <span className={`text-lg ${getTrendColor(record.composite_score, prevRecord?.composite_score)}`}>
                        {getTrendIndicator(record.composite_score, prevRecord?.composite_score)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {record.session_id ? (
                        <Button
                          color="light"
                          className="text-xs py-1 px-2"
                          onClick={() => navigate(`${MainRoutes.Chat}?sessionId=${record.session_id}&name=${userName}&readonly=true`)}
                        >
                          View Chat
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="text-xs text-gray-400 mt-2 text-right">
            {scoreHistory.length} record{scoreHistory.length !== 1 ? 's' : ''} total
          </div>
        </>
      )}
    </div>
  );
};

export const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ['overview', 'progress', 'checkins', 'chat', 'screentime', 'metadata'] as const;
  type TabId = typeof validTabs[number];
  const tabParam = searchParams.get('tab') as TabId | null;
  const activeTab: TabId = tabParam && validTabs.includes(tabParam) ? tabParam : 'overview';
  const setActiveTab = (tab: TabId) => {
    setSearchParams({ tab }, { replace: true });
  };

  const userDetails = useQuery(api.users.getUserDetails,
    userId ? { id: userId as Id<"users"> } : "skip"
  );
  const weatherQuote = useInnerWeatherQuote(
    userDetails?.userProgress?.n_score ?? 50,
    userDetails?.userProgress?.wb_score ?? 50,
    userDetails?.userProgress?.se_score ?? 50
  );
  const scoreHistory = useQuery(
    api.user_progress.getScoreHistory,
    userId ? { user_id: userId as Id<"users">, limit: 100 } : "skip"
  );
  const screenTimeData = useQuery(
    api.analytics.getUserRouteBreakdown,
    userId ? { user_id: userId as Id<"users"> } : "skip"
  );

  if (!userId) {
    return (
      <div className="px-6 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">User ID Required</h1>
          <Button color="light" onClick={() => navigate(AdminRoutes.Users)}>
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="px-6 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading user details...</div>
        </div>
      </div>
    );
  }

  const { user, userMetadata, userProgress, userSessions, personalAssistant, resumeText, clientInfo } = userDetails;

  // Get skills with progress calculated from completed sessions
  const skills = userProgress?.skills || [];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '👤' },
    { id: 'progress', label: 'Progress', icon: '📊' },
    { id: 'checkins', label: 'Check-ins', icon: '✅' },
    { id: 'chat', label: 'Chat', icon: '💬' },
    { id: 'screentime', label: 'Screen Time', icon: '⏱' },
    { id: 'metadata', label: 'Metadata', icon: '📝' }
  ];

  return (
    <div className="min-h-screen bg-background pt-24 px-6 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
          <p className="text-gray-600 mt-1">{user.email}</p>
        </div>
        <div className="flex gap-3">
          <Button 
            color="light" 
            onClick={() => navigate(AdminRoutes.Users)}
          >
            Back to Users
          </Button>
          <Button 
            color="light" 
            onClick={() => navigate(MainRoutes.Admin)}
          >
            Admin Dashboard
          </Button>
        </div>
      </div>

      {/* Basic User Info */}
      <div className="bg-primary-1 p-6 mb-6 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">User ID</h3>
            <p className="font-mono text-sm text-gray-900 mt-1">{user._id}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Name</h3>
            <p className="text-gray-900 mt-1">
              {user.firstName && user.lastName 
                ? `${user.firstName} ${user.lastName}`
                : user.firstName || user.lastName || 'Not provided'
              }
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Email</h3>
            <p className="text-gray-900 mt-1">{user.email}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm text-center whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-5 text-primary-6'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <InfoCard title="Onboarding Answers">
              {userMetadata ? (
                <div className="space-y-3">
                  {[
                    { label: 'Nickname', value: userMetadata.nickname },
                    { label: 'Location', value: userMetadata.state },
                    { label: 'Reason for Search', value: userMetadata.reason_for_search },
                    { label: 'Search Focus', value: userMetadata.irl_search_phase },
                    { label: 'Search Duration', value: userMetadata.search_duration },
                    { label: 'Readiness (1-10)', value: userMetadata.how_ready_to_start_search },
                    { label: 'Disliked Aspects', value: userMetadata.disliked_aspects_of_search },
                    { label: 'How Heard About Us', value: userMetadata.how_did_you_hear_about_us },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between text-sm">
                      <span className="text-gray-600">{label}:</span>
                      <span className="text-gray-900 text-right ml-4">{value || <span className="text-gray-400 italic">Not provided</span>}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="text-gray-600">Onboarding Completed:</span>
                    <Badge color={userMetadata.onboarding_completed ? 'green' : 'yellow'}>
                      {userMetadata.onboarding_completed ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 italic">No onboarding data available</p>
              )}
            </InfoCard>

            <InfoCard title="Skill Progress">
              {skills.length > 0 ? (
                <div className="space-y-4">
                  {skills.map((skill) => (
                    <div key={skill.skill_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-gray-900">
                          {skill.sequence}. {skill.skill_info?.name || 'Unknown Skill'}
                        </h4>
                        <Badge color={skill.percentage_complete === 100 ? 'green' : skill.percentage_complete > 0 ? 'blue' : 'zinc'}>
                          {Math.round(skill.percentage_complete)}%
                        </Badge>
                      </div>
                      
                      {/* Current Session Info for Active Sessions */}
                      {userProgress?.current_session_id && skill.percentage_complete > 0 && skill.percentage_complete < 100 && (
                        <CurrentSessionDisplay 
                          userId={userId as Id<"users">} 
                          sessionId={userProgress.current_session_id}
                        />
                      )}
                      
                      <div className="space-y-2 mt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Completed:</span>
                          <span className="text-gray-900">
                            {skill.completed_count} / {skill.total_count} assistants
                          </span>
                        </div>
                        {skill.completed_timestamp && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Finished:</span>
                            <span className="text-gray-900">
                              {new Date(skill.completed_timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No skills in progress</p>
              )}
            </InfoCard>

            <InfoCard title="Session Summary">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Sessions:</span>
                  <Badge color="blue">{userSessions.length}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Completed Sessions:</span>
                  <Badge color="green">
                    {userSessions.filter(s => s.isComplete).length}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Active Sessions:</span>
                  <Badge color="orange">
                    {userSessions.filter(s => !s.isComplete).length}
                  </Badge>
                </div>
              </div>
            </InfoCard>

            <InfoCard title="Client Info">
              {clientInfo && (clientInfo.timezone || clientInfo.language || clientInfo.screenResolution || clientInfo.userAgent) ? (
                <div className="space-y-3">
                  {clientInfo.timezone && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Timezone:</span>
                      <span className="text-gray-900">{clientInfo.timezone}</span>
                    </div>
                  )}
                  {clientInfo.language && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Language:</span>
                      <span className="text-gray-900">{clientInfo.language}</span>
                    </div>
                  )}
                  {clientInfo.screenResolution && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Screen:</span>
                      <span className="text-gray-900">{clientInfo.screenResolution}</span>
                    </div>
                  )}
                  {clientInfo.userAgent && (
                    <div className="text-sm">
                      <span className="text-gray-600">Browser:</span>
                      <p className="text-gray-900 text-xs mt-1 break-all">{clientInfo.userAgent}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 italic text-sm">No client data available — will populate on next login</p>
              )}
            </InfoCard>

            <InfoCard title="Inner Weather">
              {userProgress ? (
                <div className="space-y-4">
                  <div className="w-full h-48 mb-4">
                    <InnerWeatherIcon
                      a={userProgress.n_score}
                      b={userProgress.wb_score}
                      c={userProgress.se_score}
                      className="w-full h-full"
                    />
                  </div>
                  {weatherQuote && (
                    <div className="text-center">
                      <div className="inline-block bg-white rounded-2xl px-4 py-3 border">
                        <p className="text-lg font-light text-gray-700 leading-relaxed">
                          "{weatherQuote.quote}"
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          — {weatherQuote.author}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-xl font-bold text-primary-6">{userProgress.n_score}</div>
                      <div className="text-sm text-gray-600">N Score</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-xl font-bold text-primary-6">{userProgress.wb_score}</div>
                      <div className="text-sm text-gray-600">WB Score</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-xl font-bold text-primary-6">{userProgress.se_score}</div>
                      <div className="text-sm text-gray-600">SE Score</div>
                    </div>
                  </div>
                  {scoreHistory && scoreHistory.length > 0 && (
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-600">Composite Score</span>
                        <span className="flex items-center gap-2">
                          <span className="text-lg font-bold text-primary-6">
                            {scoreHistory[scoreHistory.length - 1].composite_score.toFixed(1)}
                          </span>
                          {scoreHistory.length >= 2 && (
                            <MindsetTrendChart data={scoreHistory} />
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 italic">No progress data available</p>
              )}
            </InfoCard>

          </div>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <div className="space-y-6">

            <InfoCard title="Skill Progress Summary">
              {skills.length > 0 ? (
                <div className="space-y-4">
                  {skills.map((skill) => (
                    <div key={skill.skill_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-gray-900">{skill.skill_info?.name || 'Unknown Skill'}</h4>
                        <Badge color={skill.percentage_complete === 100 ? 'green' : 'blue'}>
                          {Math.round(skill.percentage_complete)}%
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Sequence:</span>
                          <span className="text-gray-900">{skill.sequence}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Progress:</span>
                          <span className="text-gray-900">
                            {skill.completed_count} / {skill.total_count} assistants completed
                          </span>
                        </div>
                        {skill.completed_timestamp && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Completed:</span>
                            <span className="text-gray-900">
                              {new Date(skill.completed_timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No skills in progress</p>
              )}
            </InfoCard>

            {/* Mindset Score History */}
            <InfoCard title="Mindset Score History">
              <MindsetScoreHistoryTable userId={userId as Id<"users">} userName={`${user.firstName || ''} ${user.lastName || ''}`.trim()} />
            </InfoCard>

            {/* Completed Sessions Table */}
            <InfoCard title="Completed Sessions">
              <CompletedSessionsTable userId={userId as Id<"users">} />
            </InfoCard>
          </div>
        )}

        {/* Check-ins Tab */}
        {activeTab === 'checkins' && (
          <div className="space-y-6">
            <InfoCard title="User Check-ins">
              <UserCheckinsTable userId={userId as Id<"users">} />
            </InfoCard>
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="space-y-6">
            <InfoCard title="Evergreen Chat">
              <EvergreenChatViewer userId={userId as Id<"users">} />
            </InfoCard>
          </div>
        )}

        {/* Screen Time Tab */}
        {activeTab === 'screentime' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            {screenTimeData && (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="bg-primary-1 rounded-lg border border-primary-4 p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{formatDuration(screenTimeData.totals.total)}</div>
                  <div className="text-sm text-gray-600 mt-1">Total Time</div>
                </div>
                <div className="bg-green-50 rounded-lg border border-green-200 p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{formatDuration(screenTimeData.totals.active)}</div>
                  <div className="text-sm text-gray-600 mt-1">Active Time</div>
                </div>
                <div className="bg-orange-50 rounded-lg border border-orange-200 p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{formatDuration(screenTimeData.totals.idle)}</div>
                  <div className="text-sm text-gray-600 mt-1">Idle Time</div>
                </div>
                <div className="bg-primary-1 rounded-lg border border-primary-4 p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{screenTimeData.totals.sessionCount}</div>
                  <div className="text-sm text-gray-600 mt-1">Sessions</div>
                </div>
              </div>
            )}

            {/* Route Breakdown */}
            <InfoCard title="Time by Page">
              {!screenTimeData || screenTimeData.routes.length === 0 ? (
                <p className="text-gray-500 italic text-center py-8">
                  No screen time data collected yet for this user.
                </p>
              ) : (
                <Table striped className="w-full">
                  <TableHead className="border-b-2 border-primary-2">
                    <TableRow>
                      <TableHeader>Route</TableHeader>
                      <TableHeader>Total</TableHeader>
                      <TableHeader>Active</TableHeader>
                      <TableHeader>Idle</TableHeader>
                      <TableHeader>Views</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {screenTimeData.routes.map((row) => (
                      <TableRow key={row.route} className="hover:bg-gray-50">
                        <TableCell className="font-mono text-sm font-medium">{row.route}</TableCell>
                        <TableCell className="font-mono text-sm">{formatDuration(row.total_ms)}</TableCell>
                        <TableCell className="font-mono text-sm text-green-600">{formatDuration(row.active_ms)}</TableCell>
                        <TableCell className="font-mono text-sm text-orange-500">{formatDuration(row.idle_ms)}</TableCell>
                        <TableCell className="text-sm font-semibold">{row.views}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </InfoCard>
          </div>
        )}

        {/* Metadata Tab */}
        {activeTab === 'metadata' && (
           <div className="space-y-6">
             <InfoCard title="User Metadata">
               {userMetadata ? (
                 <div className="bg-primary-1 rounded-lg border border-primary-4">
                   <Table striped className="w-full">
                     <TableHead className="border-b-2 border-primary-2">
                       <TableRow>
                         <TableHeader>Property</TableHeader>
                         <TableHeader>Value</TableHeader>
                       </TableRow>
                     </TableHead>
                                           <TableBody>
                        {Object.entries(userMetadata).map(([key, value]) => {
                          if (!value) return null;
                          
                          // Format value for display (handle semicolon-separated lists)
                          const formatMetadataValue = (val: string) => {
                            if (val.includes(';')) {
                              return val.split(';')
                                .map(item => item.trim())
                                .filter(item => item.length > 0)
                                .join(', ');
                            }
                            return val;
                          };
                          
                          return (
                            <TableRow key={key} className="hover:bg-gray-50">
                              <TableCell className="font-medium text-gray-700">{key}</TableCell>
                              <TableCell className="text-gray-800 text-wrap">{formatMetadataValue(String(value))}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                   </Table>
                 </div>
               ) : (
                 <p className="text-gray-500 italic">No metadata available</p>
               )}
             </InfoCard>

            {resumeText && (
              <InfoCard title="Resume Text">
                <div className="max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded">
                    {resumeText.text}
                  </pre>
                </div>
              </InfoCard>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
